import React, { useMemo, useRef } from 'react';
import styled from 'styled-components';
import * as size from '@constant/size';
import * as color from '@constant/color';
import { useTypedDispatch, useTypedSelector } from '@hook/common/useStore';
import TextBlock from '@component/common/block/text-block';
import { TIyagigoSubMenu } from '@datatype/iyagigo/type';
import { actions as modalActions } from '@store/slice/modal-slice';
import { actions as workbookActions } from '@store/slice/workbook-slice';
import { EWorkbookStudyType } from '@datatype/iyagigo/type';
import {
  useModifyClozeWorkbookMutation,
  useBookmarkClozeWorkbookQuery,
  useIncorrectNoteClozeWorkbookQuery,
} from '@hook/iyagigo/useApi';
import { getClozeTypeBySubject } from '@util/workbook';
import IconBlock from '@component/common/block/icon-block';
import { useNavigate } from 'react-router-dom';
import { createClozePath } from '@constant/iyagigo/route-path';
import { useMySubscriptionQuery } from '@hook/billing/useApi';
import { EApiSubscriptionStatus } from '@datatype/billing/api-type';
import { EApiLanguageCode } from '@datatype/iyagigo/api-type';
import { useInfiniteScroll } from '@hook/common/useScroll';
import NoteTemplate, { INoteItem } from '@component/iyagigo/common/note-template';

const DescriptionContainer = styled.div`
  display: flex;
  flex-flow: row wrap;
  justify-content: flex-start;
  align-items: center;
  padding: ${size.MARGIN_1};
  background-color: ${color.BROWN_4};
  width: 100%;
`;

const KeywordContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: flex-end;
  gap: ${size.MARGIN_1};
  width: 100%;
  min-width: 0;
`;

const MeaningContainer = styled.div`
  flex: 1;
  min-width: 0;
`;

const ClozeNote: React.FC = () => {
  // ############### state ###############
  const dispatch = useTypedDispatch();
  const studyLanguage = useTypedSelector((state) => state.rootReducer?.studentReducer?.currentStudyLanguage) ?? null;
  const workbookStudyType =
    useTypedSelector((state) => state.rootReducer?.workbookReducer?.workbookStudyType) ??
    EWorkbookStudyType.BOOKMARK_NOTE;
  const isSubscriber = useMySubscriptionQuery().data?.data?.status === EApiSubscriptionStatus.ACTIVE;
  const todaySubMenu = useTypedSelector(
    (state) => state.rootReducer?.navigationReducer?.iyagigo?.subMenu,
  ) as TIyagigoSubMenu['cloze'];

  // ############### hook ###############
  const navigate = useNavigate();

  // ############### api ###############
  const clozeType = getClozeTypeBySubject(todaySubMenu);
  const languageCode =
    studyLanguage === EApiLanguageCode.ENGLISH ? EApiLanguageCode.ENGLISH : EApiLanguageCode.JAPANESE;

  const mutation = useModifyClozeWorkbookMutation(clozeType, languageCode);
  const bookmarkWorkbookQuery = useBookmarkClozeWorkbookQuery(clozeType, languageCode);
  const bookmarkRepeatQuery = useBookmarkClozeWorkbookQuery(clozeType, languageCode, true);
  const incorrectNoteWorkbookQuery = useIncorrectNoteClozeWorkbookQuery(clozeType, languageCode);
  const incorrectNoteRepeatQuery = useIncorrectNoteClozeWorkbookQuery(clozeType, languageCode, true);
  const workbookQuery =
    workbookStudyType === EWorkbookStudyType.INCORRECT_NOTE ? incorrectNoteWorkbookQuery : bookmarkWorkbookQuery;
  const workbookData = useMemo(
    () => workbookQuery.data?.pages.flatMap((page) => page.data ?? []) ?? [],
    [workbookQuery.data],
  );
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const { sentinelRef } = useInfiniteScroll({
    hasNextPage: workbookQuery.hasNextPage ?? false,
    isFetchingNextPage: workbookQuery.isFetchingNextPage ?? false,
    fetchNextPage: workbookQuery.fetchNextPage,
    root: scrollContainerRef,
  });

  // ############### handler ###############
  const handleRepeatToggle = async (workbookKey: string, currentRepeat: boolean) => {
    let request;
    switch (workbookStudyType) {
      case EWorkbookStudyType.BOOKMARK_NOTE:
        request = { workbookKey, isBookmarkRepeat: !currentRepeat };
        break;
      case EWorkbookStudyType.INCORRECT_NOTE:
        request = { workbookKey, isIncorrectRepeat: !currentRepeat };
        break;
      default:
        return;
    }
    await mutation.mutateAsync(request);
    switch (workbookStudyType) {
      case EWorkbookStudyType.BOOKMARK_NOTE:
        bookmarkRepeatQuery.refetch();
        break;
      case EWorkbookStudyType.INCORRECT_NOTE:
        incorrectNoteRepeatQuery.refetch();
        break;
    }
  };

  const handleRemoveClick = (workbookKey: string) => {
    const isIncorrect = workbookStudyType === EWorkbookStudyType.INCORRECT_NOTE;
    dispatch(
      modalActions.showDialogModal({
        type: 'remove-confirm',
        data: { workbookKey, domain: 'cloze', isIncorrect },
      }),
    );
  };

  const repeatCount = workbookData.filter((item) => {
    switch (workbookStudyType) {
      case EWorkbookStudyType.BOOKMARK_NOTE:
        return item.isBookmarkRepeat;
      case EWorkbookStudyType.INCORRECT_NOTE:
        return item.isIncorrectRepeat;
      default:
        return false;
    }
  }).length;

  const handleRepeatStudyClick = () => {
    // 무료회원 구독 Wall (access) — 반복학습은 Premium 전용
    if (!isSubscriber) {
      dispatch(
        modalActions.showWallToast({
          type: 'subscription-wall',
          data: { trigger: 'access' },
        }),
      );
      return;
    }

    switch (workbookStudyType) {
      case EWorkbookStudyType.BOOKMARK_NOTE:
        dispatch(workbookActions.configureBookmarkWorkbook({}));
        break;
      case EWorkbookStudyType.INCORRECT_NOTE:
        dispatch(workbookActions.configureIncorrectWorkbook({}));
        break;
    }
    navigate(createClozePath('study'));
  };

  const handleItemClick = (workbookKey: string) => {
    switch (workbookStudyType) {
      case EWorkbookStudyType.BOOKMARK_NOTE:
        dispatch(workbookActions.configureBookmarkWorkbook({ workbookKey }));
        break;
      case EWorkbookStudyType.INCORRECT_NOTE:
        dispatch(workbookActions.configureIncorrectWorkbook({ workbookKey }));
        break;
    }
    navigate(createClozePath('study'));
  };

  // TODO: 난이도 정렬/ 학습일자 정렬/ 그룹
  const items: INoteItem[] = workbookData.map((clozeNoteWorkbook) => {
    let isRepeat;
    switch (workbookStudyType) {
      case EWorkbookStudyType.BOOKMARK_NOTE:
        isRepeat = !!clozeNoteWorkbook.isBookmarkRepeat;
        break;
      case EWorkbookStudyType.INCORRECT_NOTE:
        isRepeat = !!clozeNoteWorkbook.isIncorrectRepeat;
        break;
      default:
        isRepeat = false;
        break;
    }

    return {
      key: clozeNoteWorkbook.key,
      isRepeat,
      itemContent: (
        <>
          <KeywordContainer>
            <TextBlock
              text={clozeNoteWorkbook.cloze.sourceKeyword ?? ''}
              design={{ text: { size: 'medium', color: color.BLACK_2, bold: true } }}
            />
            <MeaningContainer>
              <TextBlock
                text={clozeNoteWorkbook.cloze.keywordMeaningKo ?? ''}
                design={{
                  text: { size: 'small', color: color.GRAY_1, bold: true, oneline: true },
                  box: { width: '100%', alignX: 'left' },
                }}
              />
            </MeaningContainer>
          </KeywordContainer>
          <TextBlock
            text={
              studyLanguage === EApiLanguageCode.ENGLISH
                ? clozeNoteWorkbook.cloze.sentenceEn
                : clozeNoteWorkbook.cloze.sentenceJa
            }
            design={{
              text: { size: 'medium', color: color.GRAY_1, oneline: true },
              box: { width: '100%', alignX: 'left' },
            }}
          />
        </>
      ),
    };
  });

  const description = (
    <DescriptionContainer>
      <TextBlock
        text={
          workbookStudyType === EWorkbookStudyType.INCORRECT_NOTE
            ? '틀린 문제를 <strong>반복학습 </strong>'
            : '저장한 문제로 <strong>반복학습 </strong>'
        }
        design={{ text: { size: 'medium', color: color.BLACK_2 } }}
      />
      <IconBlock icon='repeat' design={{ icon: { size: 'medium', color: color.BROWN_1, kind: 'outlined' } }} />
      <TextBlock text=' 해보세요' design={{ text: { size: 'medium', color: color.BLACK_2 } }} />
    </DescriptionContainer>
  );

  return (
    <NoteTemplate
      description={description}
      isPending={workbookQuery.isPending}
      items={items}
      emptyText={
        workbookStudyType === EWorkbookStudyType.INCORRECT_NOTE ? '틀린 문제가 없어요' : '저장한 문제가 없어요'
      }
      repeatStudyDisabled={repeatCount === 0}
      sentinelRef={sentinelRef}
      scrollContainerRef={scrollContainerRef}
      handleRepeatToggle={handleRepeatToggle}
      handleItemClick={handleItemClick}
      handleRemoveClick={handleRemoveClick}
      handleRepeatStudyClick={handleRepeatStudyClick}
    />
  );
};

export default ClozeNote;
